/**
 * CheckInAIAssessment
 * Strip rendered inside CheckInTimelineItem. Shows the AI's quality grade + one-line
 * summary + flags. If missing, admins can manually trigger generation.
 */

import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles, AlertTriangle, Loader2 } from 'lucide-react';
import { aiCoachService, type CheckInAssessment } from '../../services/aiCoach.service';
import { useCanUseAI } from '../../hooks/useAIConfig';
import { PURPLE } from './ai-theme';

interface Props {
  checkInId: string;
  assessment?: CheckInAssessment | null;
}

const qualityLabel = (q: CheckInAssessment['quality']) =>
  q === 'good' ? 'Good' : q === 'fair' ? 'Fair' : 'Poor';

const qualityTone = (q: CheckInAssessment['quality']) =>
  q === 'good' ? 'text-emerald-700' : q === 'fair' ? 'text-amber-700' : 'text-red-700';

export const CheckInAIAssessment: React.FC<Props> = ({ checkInId, assessment }) => {
  const qc = useQueryClient();
  const { isEligible, aiEnabled } = useCanUseAI();
  const [local, setLocal] = useState<CheckInAssessment | null>(assessment ?? null);
  const [generating, setGenerating] = useState(false);

  // Viewers (and unauthenticated) never see the row.
  if (!isEligible && !local) return null;
  // For eligible users (admins + managers + employees), hide the "Generate now"
  // affordance when the tenant has AI off and there's no prior assessment to display.
  if (!aiEnabled && !local) return null;

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const result = await aiCoachService.assessCheckIn(checkInId);
      setLocal(result);
      qc.invalidateQueries({ queryKey: ['checkIns'] });
    } catch (err) {
      const e = err as { response?: { data?: { message?: string; code?: string } } };
      const code = e?.response?.data?.code;
      if (code === 'AI_NOT_CONFIGURED') {
        toast.error('AI is not configured. Add a key in Settings.');
      } else {
        toast.error(e?.response?.data?.message || 'Failed to generate assessment');
      }
    } finally {
      setGenerating(false);
    }
  };

  if (!local) {
    return (
      <div className="mt-3 border-t border-stone-100 pt-2 flex items-center gap-2 text-[13px] text-stone-500">
        <Sparkles className="w-3.5 h-3.5" style={{ color: PURPLE }} />
        <span>AI assessment pending.</span>
        <button
          type="button"
          onClick={handleGenerate}
          disabled={generating}
          className="font-medium underline disabled:opacity-50"
          style={{ color: PURPLE }}
        >
          {generating ? 'Generating…' : 'Generate now'}
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 border-t border-stone-100 pt-2 flex gap-2 items-start">
      {generating ? (
        <Loader2 className="w-3.5 h-3.5 mt-0.5 shrink-0 animate-spin" style={{ color: PURPLE }} />
      ) : (
        <Sparkles className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: PURPLE }} />
      )}
      <div className="text-[13px] text-stone-600 leading-snug flex-1">
        <span className={`font-semibold ${qualityTone(local.quality)}`}>
          Action quality: {qualityLabel(local.quality)}
        </span>
        {local.summary && <> — {local.summary}</>}
        {local.flags && local.flags.length > 0 && (
          <ul className="mt-1 space-y-0.5">
            {local.flags.map((f, i) => (
              <li key={i} className="flex items-start gap-1 text-[12px] text-amber-700">
                <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};
